import { Box, Button, FormControl, Typography } from '@mui/material'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { onSaveEntityFields } from '../../../../services/entityServices'
import { updateLoader } from '../../../../redux/slices/loaderSlice'
import {
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
    EntityStatus,
    EntityStepTypes,
    ShipmentAttributes,
} from '../../../../constants/constants'
import { IEntityTypeAttributeValue } from '../../../DTStudio/interfaces'
import SubTemplatesList from '../../../Common/SubTemplatesList'
import {
    fetchEntityInstanceData,
    fetchRulesetsForProduct,
} from '../../../../services/entityInstanceServices'
import {
    updateAttributeValues,
    updateValuesForPayload,
} from '../../../../utils/EntityAttributesUtils'
import { store } from '../../../../redux/store'
import Fields from '../../../Common/Fields'
import { useTranslation } from 'react-i18next'
import SearchInAPIAndSelectInput from '../../../Common/SearchInAPIAndSelectInput'
import SearchInAPIAndMultiSelectInput from '../../../Common/SearchInAPIAndMultiSelectInput'
import { validateAttributes } from '../../../../utils/attributeValidations'
import { formatEntityDisplayLabel } from '../../../../utils/entityDisplayUtils'
import { useEntityRecords } from '../../../../hooks/useEntityRecords'

// Immutable attribute key (the `id` used in the entity's attributeValues map)
// that holds the list of emails a shipment is shared with. When a selected
// entity (e.g. a Receiver) carries a non-empty value for this attribute, it is
// auto-populated onto the parent (shipment) attribute with the same key.
const SHARE_LIST_ATTRIBUTE_KEY = ShipmentAttributes.ShareListAttributeId

interface EntityTypeAttributeProps {
    label: string
    id: string
    value: any
    onChange: any
    type: string
    errorMessage?: string
    required?: boolean
    disabled?: boolean
    templateId: string
    isArray: boolean
    orgId: string
}

interface AddNewEntityDetails {
    templateId: string
    templateName: string
    entityId: string
    attributes: any[]
}

const EntityTypeAttribute = (props: EntityTypeAttributeProps): ReactElement => {
    const {
        label,
        id,
        value,
        onChange,
        type,
        errorMessage,
        required,
        disabled,
        templateId,
        isArray = false,
        orgId,
    } = props
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)
    const entitySteps = useAppSelector(
        (state) => state.entityInstance.entitySteps,
    )

    const dispatch = useAppDispatch()

    const [selectedValues, setSelectedValues] = useState<
        IEntityTypeAttributeValue[]
    >([]) // this for multi select

    const [selectedValue, setSelectedValue] = useState<any>({}) // this for single select //SearchInAPIAndSelectInput this component need this

    //if it is entity json we can add new entity in inline
    const [openSubTemplates, setOpenSubTemplates] = useState(false)

    const [newEntityDetails, setNewEntityDetails] =
        useState<AddNewEntityDetails>()

    const templateOrgId = useMemo(() => {
        return orgId || org?.id || ''
    }, [orgId, org?.id])

    const {
        recordsList,
        isFetching,
        hasMore,
        hasAddNew,
        onSearch,
        fetchNextPage,
    } = useEntityRecords({ templateId, orgId: templateOrgId, id })

    const getDisplayLabel = useCallback(
        (option: any) => formatEntityDisplayLabel(option),
        [],
    )

    useEffect(() => {
        if (isArray) {
            setSelectedValues(Array.isArray(value) ? value : [])
        } else {
            setSelectedValue(value ?? null)
        }
    }, [value, isArray])

    useEffect(() => {
        if (newEntityDetails?.templateId) {
            onChange(id, '100%', 'width')
        } else {
            onChange(id, '', 'width')
        }
    }, [newEntityDetails, value, id])

    const onSelectRecord = (id: string, item: any) => {
        onChangeRecord(id, item?.entityId ? [item] : [])
    }

    const onSelectRecords = (id: string, items: any) => {
        onChangeRecord(id, items)
    }

    const onChangeRecord = async (id: string, selectedList: any[]) => {
        dispatch(updateLoader(true))
        //if attribute is product. we need to add product rulesets to shipment rules.
        let productRulesetsValue: { id: string; name: string }[] = []
        const updatedEntityIdsData = await Promise.all(
            selectedList?.map(async (item: any) => {
                if (
                    type.toLowerCase() === AttributeType.ENTITY &&
                    id === ShipmentAttributes.ProductAttributeId
                ) {
                    //get Rulesets Selecte for Product
                    const productRulesets = await fetchRulesetsForProduct(
                        templateOrgId,
                        item.entityId,
                    )
                    productRulesetsValue = [
                        ...productRulesetsValue,
                        ...productRulesets,
                    ]
                }
                if (type.toLowerCase() === AttributeType.Entity_JSON) {
                    const obj: any = {
                        ...item.attributes,
                        uid: item.uid,
                        entityId: item.entityId,
                        templateId: item.templateId || '',
                        templateName: item.templateName || '',
                    }
                    return { ...obj }
                } else {
                    return {
                        uid: item.uid,
                        entityId: item.entityId,
                        templateId: item.templateId || '',
                        templateName: item.templateName || '',
                        attributes: item.attributes,
                    } as IEntityTypeAttributeValue
                }
            }),
        )

        //updating value
        const updatedSelectedValue = isArray
            ? updatedEntityIdsData
            : updatedEntityIdsData[0] ?? null
        if (isArray) {
            setSelectedValues(updatedSelectedValue)
        } else {
            setSelectedValue(updatedSelectedValue)
        }
        if (
            type.toLowerCase() === AttributeType.ENTITY &&
            id === ShipmentAttributes.ProductAttributeId
        ) {
            //updating Business rulesets filed value

            //first we need find out unselected product rulesets. to remove from business ruleset filed value
            const exsitingValues = isArray
                ? selectedValues
                : selectedValue
                  ? [selectedValue]
                  : []
            const unSelectedEntities = exsitingValues?.filter(
                (a: any) =>
                    !updatedEntityIdsData?.some(
                        (b: any) => a.entityId === b.entityId,
                    ),
            )
            //unselected product rulesets
            let unSelectedProductRulesetsValue: { id: string; name: string }[] =
                []
            for (const item of unSelectedEntities) {
                if (item.entityId) {
                    //get Rulesets Selecte for Product
                    const unSelectedproductRulesets =
                        await fetchRulesetsForProduct(
                            templateOrgId,
                            item.entityId,
                        )
                    unSelectedProductRulesetsValue = [
                        ...unSelectedProductRulesetsValue,
                        ...unSelectedproductRulesets,
                    ]
                }
            }
            //business rules filed
            const BusinessRulesField = entitySteps
                ?.find((i) => i.stepType === EntityStepTypes.Attributes)
                ?.attributes?.find(
                    (item) => item.id === BUSINESS_RULES_FIELD_ID,
                )
            let updatedBusinessRulesFieldValue = BusinessRulesField?.value || []
            //remove relusets related to unselcted product
            // if it's defult we don't remove
            const defaultBusinessRules = BusinessRulesField?.defaultRulesets
            const updatedUnSelectedProductRulesetsValue =
                unSelectedProductRulesetsValue.filter(
                    (a) =>
                        !defaultBusinessRules?.some((b: any) => a.id === b.id),
                )
            updatedBusinessRulesFieldValue =
                updatedBusinessRulesFieldValue.filter(
                    (a: any) =>
                        !updatedUnSelectedProductRulesetsValue?.some(
                            (b) => a.id === b.id,
                        ),
                )
            //updating business rules filed value with new product rulesets
            updatedBusinessRulesFieldValue = [
                ...updatedBusinessRulesFieldValue,
                ...productRulesetsValue,
            ]
            //remove if any common rulesets are added
            const updatedBusinessRulesFieldValues = [
                ...new Map(
                    updatedBusinessRulesFieldValue.map((item: any) => [
                        item.id,
                        item,
                    ]),
                ).values(),
            ]
            onChange(
                id,
                updatedSelectedValue,
                'value',
                BUSINESS_RULES_FIELD_ID,
                updatedBusinessRulesFieldValues,
                'value',
            )
        } else {
            // Auto-populate the shipment's Share List from the selected entity
            // (e.g. a Receiver) when it carries a non-empty shareList. Records
            // are fetched with includeAttributes, so the value is already on the
            // selected item's attributes map — no extra request needed.
            const selectedItems = isArray
                ? selectedList
                : selectedList?.slice(0, 1)
            const shareListValue = selectedItems
                ?.map((it: any) => it?.attributes?.[SHARE_LIST_ATTRIBUTE_KEY])
                ?.find((v: any) => Array.isArray(v) && v.length > 0)

            if (Array.isArray(shareListValue) && shareListValue.length > 0) {
                onChange(
                    id,
                    updatedSelectedValue,
                    'value',
                    SHARE_LIST_ATTRIBUTE_KEY,
                    shareListValue,
                    'value',
                )
            } else {
                onChange(id, updatedSelectedValue)
            }
        }
        dispatch(updateLoader(false))
    }

    const onSaveRecord = (savedRecord: any) => {
        let updateSavedRecord = []
        if (isArray) {
            const index = selectedValues.findIndex(
                (i) => i.entityId === savedRecord.entityId,
            )
            if (index !== -1) {
                updateSavedRecord = selectedValues.map((i) => {
                    if (i.entityId === savedRecord.entityId) {
                        return { ...savedRecord }
                    } else {
                        return { ...i }
                    }
                })
            } else {
                updateSavedRecord = [...selectedValues, savedRecord]
            }
        } else {
            updateSavedRecord = [savedRecord]
        }
        onChangeRecord(id, updateSavedRecord)
    }

    const onClickAddNewInlineEntityJson = () => {
        setOpenSubTemplates(true)
    }

    const onCloseSubTemplatePopUp = () => {
        setOpenSubTemplates(false)
    }

    const onSubmitSubTemplatePopUp = (templateDetails: {
        templateId: string
        templateName: string
    }) => {
        getAndSetTheAddOrEditEntityDetails(templateDetails)
    }

    const getAndSetTheAddOrEditEntityDetails = async (templateDetails: {
        templateId: string
        templateName: string
        entityId?: string
    }) => {
        dispatch(updateLoader(true))
        const EntityInstanceResponse: any = await fetchEntityInstanceData(
            templateOrgId,
            templateDetails?.templateId,
            templateDetails?.entityId || '',
        )
        const updatedAttributesList = updateAttributeValues(
            EntityInstanceResponse?.attributes || [],
            store,
            !!templateDetails?.entityId,
        )
        const updateNewEntityDetails: AddNewEntityDetails = {
            templateId: templateDetails?.templateId,
            templateName: templateDetails?.templateName,
            entityId: templateDetails?.entityId || '0',
            attributes: updatedAttributesList,
        }
        setNewEntityDetails(updateNewEntityDetails)

        setOpenSubTemplates(false)
        dispatch(updateLoader(false))
    }

    const onClickRecordEdit = (rec: any) => {
        getAndSetTheAddOrEditEntityDetails(rec)
    }

    const onChangeNewEntityFields = (
        id: string,
        value: string,
        key?: string,
    ) => {
        const updateNewEntityDetails = {
            ...newEntityDetails,
            attributes: newEntityDetails?.attributes.map((i) => {
                if (i.id === id) {
                    const obj = { ...i }
                    obj[key ?? 'value'] = value
                    return obj
                } else {
                    return { ...i }
                }
            }),
        }
        setNewEntityDetails(updateNewEntityDetails as AddNewEntityDetails)
    }

    const onClickSaveNewEntity = () => {
        dispatch(updateLoader(true))
        const { isValidated, updatedFields } = validateAttributes(
            newEntityDetails?.attributes || [],
        )
        if (isValidated) {
            onSaveNewEntity()
        } else {
            setNewEntityDetails({
                ...newEntityDetails,
                attributes: updatedFields || [],
            } as AddNewEntityDetails)
            dispatch(updateLoader(false))
        }
    }

    const onSaveNewEntity = async () => {
        const updatedAttibutes = await updateValuesForPayload(
            newEntityDetails?.attributes,
        )
        const sendData = [
            {
                stepType: EntityStepTypes.Attributes,
                attributes: updatedAttibutes,
            },
        ]

        const result = await onSaveEntityFields(
            sendData,
            templateOrgId,
            newEntityDetails?.templateId || '',
            newEntityDetails?.entityId || '0',
            EntityStatus.ACTIVE,
            org?.id || '',
        )
        if (result?.type == 'success') {
            const UIdAttributes = updatedAttibutes.filter(
                (i: any) => i.metadata?.Displayable && i.value,
            )
            const UId = UIdAttributes.map((item: any) => {
                if (
                    item.dataType.toLowerCase() === AttributeType.Entity_JSON ||
                    item.dataType.toLowerCase() === AttributeType.ENTITY ||
                    item.dataType.toLowerCase() === AttributeType.JSON
                ) {
                    return item.value?.uid || ''
                } else {
                    return item.value
                }
            })?.join(',')
            const updatedAttibutesObj: any = {}
            updatedAttibutes.forEach((i: any) => {
                updatedAttibutesObj[i.id] = i.value
            })
            const addedRecordData = {
                uid: UId,
                entityId: result?.payload?.entityId || '0',
                templateId: newEntityDetails?.templateId || '',
                templateName: newEntityDetails?.templateName || '',
                attributes: updatedAttibutesObj,
            }
            onSaveRecord(addedRecordData)
            setNewEntityDetails(undefined)
        }
        dispatch(updateLoader(false))
    }

    return (
        <Box>
            <Box>
                <FormControl fullWidth disabled={!!disabled} size="small">
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                width: ' 100%',
                                maxWidth: `${newEntityDetails ? 'calc(50% - 20px)' : '100%'}`,
                            }}
                        >
                            {isArray ? (
                                <SearchInAPIAndMultiSelectInput
                                    id={id}
                                    label={label}
                                    placeholder={t('selectLabel', { label })}
                                    required={required}
                                    value={selectedValues}
                                    onSelect={onSelectRecords}
                                    list={recordsList}
                                    errorMessage={
                                        !selectedValues.length && errorMessage
                                            ? errorMessage
                                            : ''
                                    }
                                    disabled={disabled}
                                    hasAddNew={hasAddNew}
                                    onSearch={onSearch}
                                    onFetchNextPage={fetchNextPage}
                                    hasMore={hasMore}
                                    isFetching={isFetching}
                                    itemId="entityId"
                                    itemName="uid"
                                    getDisplayLabel={getDisplayLabel}
                                    onClickAddNew={() =>
                                        onClickAddNewInlineEntityJson()
                                    }
                                    onClickRowIcon={(r: any) =>
                                        onClickRecordEdit(r)
                                    }
                                />
                            ) : (
                                <SearchInAPIAndSelectInput
                                    id={id}
                                    label={label}
                                    placeholder={t('selectLabel', { label })}
                                    value={selectedValue}
                                    list={recordsList}
                                    required={required}
                                    disabled={disabled}
                                    onSelect={onSelectRecord}
                                    onSearch={onSearch}
                                    errorMessage={errorMessage}
                                    onFetchNextPage={fetchNextPage}
                                    hasMore={hasMore}
                                    isFetching={isFetching}
                                    hasAddNew={hasAddNew}
                                    itemId="entityId"
                                    itemName="uid"
                                    getDisplayLabel={getDisplayLabel}
                                    onClickAddNew={() => {
                                        onClickAddNewInlineEntityJson()
                                    }}
                                    onClickRowIcon={(r: any) =>
                                        onClickRecordEdit(r)
                                    }
                                />
                            )}
                        </Box>
                    </Box>
                </FormControl>
            </Box>
            {newEntityDetails && (
                <Box
                    className="childWidth100"
                    sx={{
                        background: '#00000010',
                        marginRight: '10px',
                        marginTop: '10px',
                        padding: '20px 0px 20px 20px',
                        borderRadius: '3px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ marginBottom: '15px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#000',
                            }}
                        >
                            {t('add')} {newEntityDetails?.templateName}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="flex-start"
                        justifyContent="flex-start"
                    >
                        <Fields
                            fields={newEntityDetails?.attributes}
                            onChangeFields={onChangeNewEntityFields}
                            onClickMap={() => {}}
                            disabled={false}
                            orgId={orgId}
                        />
                    </Box>
                    <Box sx={{ marginLeft: 'auto', marginRight: '20px' }}>
                        <Button
                            onClick={() => {
                                setNewEntityDetails(undefined)
                            }}
                            sx={{
                                fontSize: '12px',
                                padding: '7px 20px',
                                marginRight: '15px',
                            }}
                            variant="outlined"
                            id="inline-entity-cancel"
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => onClickSaveNewEntity()}
                            sx={{
                                width: '100px',
                                color: '#fff',
                                fontSize: '12px',
                                padding: '7px 20px',
                            }}
                            id="inline-entity-save"
                        >
                            {t('save')}
                        </Button>
                    </Box>
                </Box>
            )}
            {openSubTemplates && (
                <SubTemplatesList
                    templateId={templateId}
                    isOpen={openSubTemplates}
                    onClose={onCloseSubTemplatePopUp}
                    onSubmit={onSubmitSubTemplatePopUp}
                    submitIfSingle={true}
                    orgId={orgId}
                />
            )}
        </Box>
    )
}

export default EntityTypeAttribute
